import styled from "@emotion/styled";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { Phase } from "../../../../features/measurement/types";
import { infoText, isDeviceActive, isFinished, isRouterActive, isServerActive, selectPrepareResult } from "../../../../features/measurement/utils";
import StrokedRectangle from "./StrokedRectangle";
import { theme } from "../../../../theme/theme";
import { Kakerlake, TopLineKakerlake } from "./Kakerlake";
import ProgressAnimation from "../common/ProgressAnimation";
import NavigationButton from "../../elements/NavigationButton";
import { updatePage } from "../../../../features/pageManager/pageManagerSlice";
import { PageKey } from "../../../../features/pageManager/pageState";
import { RedButtonDiv } from "../../elements/Buttons";
import MeasurementItem from "../common/MeasurementItem";
import { getDeviceName } from "../../../../helper/getDeviceText";
import t from "../../../../helper/t";
import { isMobileDevice } from "../common/utils";
import { BottomInfoContainer } from "../mobile/MeasurementMobile";
import { BottomInfoDiv, InfoContainer, InfoKeySpan, InfoSeparatorSpan } from "../../result/currentMeasurement/resultElements";
import getSpeedtestId from "../../../../helper/utils/getSpeedtestId";
import CopyableData from "../../elements/CopyableData";
import * as R from "ramda";

import internationalSvg from "../../../../../assets/images/splus3/international.svg";
import routerSvg from "../../../../../assets/images/splus3/router.svg";
import mobileSvg from "../../../../../assets/images/splus3/mobile.svg";
import laptopSvg from "../../../../../assets/images/splus3/laptop.svg";

const MeasurementItemWidth = 144;
const MeasurementItemHeight = 136;

const MeasurementItemGap = 137;
const DashLength = 18;
const DashWidth = 1;
const AnimatedRectangleHeight = 43;
const KakerlakeLength = 14;
const KakerlakeWidth = 3;

const RootDiv = styled.div(({ theme }) => ([
  {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexGrow: 10,

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: {
      display: "none"
    },
  }
]));

const MeasurementContainer = styled.div([
  {
    marginTop: 75 - AnimatedRectangleHeight,
    display: "grid",
    gridTemplateRows: `${AnimatedRectangleHeight}px ${AnimatedRectangleHeight}px auto`,
    gridTemplateColumns: `${MeasurementItemWidth}px ${MeasurementItemGap}px ${MeasurementItemWidth}px ${MeasurementItemGap}px ${MeasurementItemWidth}px`,
  }
]);

const StrokesContainerLeft = styled.div([
  {
    gridColumnStart: 1,
    gridColumnEnd: 4,
    gridRow: 1,

    marginLeft: MeasurementItemWidth / 2 - DashWidth / 2,
    marginRight: MeasurementItemWidth / 2 - DashWidth / 2,
  },
  `
    rect {
      stroke-dashoffset: 0;
    }
  `
]);

const StrokesContainerRight = styled.div([
  {
    gridColumnStart: 3,
    gridColumnEnd: 6,
    gridRow: 1,

    marginLeft: MeasurementItemWidth / 2 - DashWidth / 2,
    marginRight: MeasurementItemWidth / 2 - DashWidth / 2,
  },
  `
    rect {
      stroke-dashoffset: 0;
    }
  `
]);

const KakerlakeContainerLeft = styled.div([
  {
    gridColumnStart: 1,
    gridColumnEnd: 4,
    gridRowStart: 1,
    gridRowEnd: 3,

    marginTop: -2,
    marginLeft: MeasurementItemWidth / 2,
    marginRight: MeasurementItemWidth / 2,
  }
]);

const KakerlakeContainerRight = styled.div([
  {
    gridColumnStart: 3,
    gridColumnEnd: 6,
    gridRowStart: 1,
    gridRowEnd: 3,

    marginTop: -2,
    marginLeft: MeasurementItemWidth / 2,
    marginRight: MeasurementItemWidth / 2,
  }
]);

const KakerlakeContainerFull = styled.div([
  {
    gridColumnStart: 1,
    gridColumnEnd: 6,
    gridRowStart: 1,
    gridRowEnd: 3,

    marginTop: -2,
    marginLeft: MeasurementItemWidth / 2,
    marginRight: MeasurementItemWidth / 2,
  }
]);

const InfoTextDiv = styled.div([
  props => props.theme.splus3.foreground.primary,
  props => props.theme.splus3.typography.large,
]);

interface ProgressContainerProps {
  isFinished: boolean;
}

const ProgressContainer = styled.div<ProgressContainerProps>(({ isFinished }) => ([
  {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 10,
    maxHeight: 290,

    ["@media (max-height: 720px)"]: {
      paddingTop: isFinished ? 16 : 0,
    }
  }
]));

const ProgressAnimationContainer = styled.div<ProgressContainerProps>(({ isFinished }) => ([
  {
    display: "flex",

    ["@media (max-height: 720px)"]: {
      display: isFinished ? "none" : "flex",
    }
  }
]));

const ButtonContainer = styled.div([
  {
    marginTop: 16,
    marginBottom: 24,
  }
]);

interface MeasurementDesktopProps {
}

const MeasurementDesktop: React.FC<MeasurementDesktopProps> = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(state => state.measurement);
  const prepareResult = selectPrepareResult(state);
  const speedtestId = prepareResult === null ? null : getSpeedtestId(prepareResult);

  const animated = () => {
    switch (state.kind) {
      case Phase.Downloading:
        const downloadingDuration = state.prepareResult.config.download.duration * 1000 / 4;
        return (<>
          <KakerlakeContainerLeft>
            <Kakerlake key="downloadAnimationLeft"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth + MeasurementItemGap}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              direction="Forward"
              duration={downloadingDuration}
              delay={0}
              repeatCount={2}
            />
          </KakerlakeContainerLeft>

          <KakerlakeContainerRight>
            <Kakerlake key="downloadAnimationRight"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth + MeasurementItemGap}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              direction="Forward"
              duration={downloadingDuration}
              delay={downloadingDuration * 1.1}
              repeatCount={2}
            />
          </KakerlakeContainerRight>
        </>);
      case Phase.Uploading:
        const uploadingDuration = state.prepareResult.config.upload.duration * 1000 / 4;
        return (<>
          <KakerlakeContainerLeft>
            <Kakerlake key="uploadAnimationLeft"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth + MeasurementItemGap}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              direction="Backward"
              duration={uploadingDuration}
              delay={uploadingDuration * 1.1}
              repeatCount={2}
            />
          </KakerlakeContainerLeft>

          <KakerlakeContainerRight>
            <Kakerlake key="uploadAnimationRight"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth + MeasurementItemGap}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              direction="Backward"
              duration={uploadingDuration}
              delay={0}
              repeatCount={2}
            />
          </KakerlakeContainerRight>
        </>);
      case Phase.Pinging:
      case Phase.Completing:
      case Phase.ModemResolving:
        const pingDuration = state.prepareResult.config.ping.duration * 1000 / 4;
        return (
          <KakerlakeContainerFull style={{ zIndex: 20 }}>
            <TopLineKakerlake key="pingAnimationTop"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth * 2 + MeasurementItemGap * 2}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              duration={pingDuration}
            />
          </KakerlakeContainerFull>
        );
      case Phase.ModemExecuting:
        return (
          <KakerlakeContainerLeft>
            <Kakerlake key="modemAnimationLeft"
              strokeColor={theme.splus3.colors.vodafoneRed}
              rectWidth={MeasurementItemWidth + MeasurementItemGap}
              rectHeight={AnimatedRectangleHeight * 2 - DashWidth / 2}
              strokeLength={KakerlakeLength}
              strokeWidth={KakerlakeWidth}
              direction="Forward"
              duration={2500}
              delay={0}
              repeatCount="indefinite"
            />
          </KakerlakeContainerLeft>
        );
      default:
        return null;
    }
  }

  const getPathOpacity = (position: "left" | "right"): number => {
    const activeOpacity = 1;
    const inactiveOpacity = 0.3;

    switch (position) {
      case "left": return isServerActive(state) ? activeOpacity : inactiveOpacity;
      case "right": return isDeviceActive(state) ? activeOpacity : inactiveOpacity;
    }
  }

  const finished = isFinished(state);

  return (
    <RootDiv>
      <MeasurementContainer>
        <StrokesContainerLeft style={{ opacity: getPathOpacity("left") }}>
          <StrokedRectangle strokeLength={DashLength} strokeWidthTop={DashWidth} strokeWidthLeft={DashWidth} strokeWidthRight={DashWidth} strokeColor={theme.splus3.colors.defaultForeground} />
        </StrokesContainerLeft>

        <StrokesContainerRight style={{ opacity: getPathOpacity("right") }}>
          <StrokedRectangle strokeLength={DashLength} strokeWidthTop={DashWidth} strokeWidthLeft={DashWidth} strokeWidthRight={DashWidth} strokeColor={theme.splus3.colors.defaultForeground} />
        </StrokesContainerRight>

        {animated()}

        <div style={{ gridColumn: 1, gridRowStart: 2, gridRowEnd: 4, zIndex: 10 }}>
          <MeasurementItem caption={t("Server")} value="Vodafone" isActive={isServerActive(state)} width={MeasurementItemWidth} height={MeasurementItemHeight}>
            <img style={{ transform: `translate(0px, 100px)` }} src={internationalSvg.src} alt="server" />
          </MeasurementItem>
        </div>
        <div style={{ gridColumn: 3, gridRowStart: 2, gridRowEnd: 4, zIndex: 10 }}>
          <MeasurementItem caption={t("Router")} value={prepareResult?.init.modem.name} isActive={isRouterActive(state)} width={MeasurementItemWidth} height={MeasurementItemHeight}>
            <img style={{ transform: `translate(0px, 93px)` }} src={routerSvg.src} alt="router" />
          </MeasurementItem>
        </div>
        <div style={{ gridColumn: 5, gridRowStart: 2, gridRowEnd: 4, zIndex: 10 }}>
          <MeasurementItem caption={t("Gerät")} value={getDeviceName(prepareResult) ?? ""} isActive={isDeviceActive(state)} width={MeasurementItemWidth} height={MeasurementItemHeight}>
            {
              isMobileDevice(prepareResult, false) ?
                <img style={{ transform: `translate(0px, 100px)` }} src={mobileSvg.src} alt="mobile" /> :
                <img style={{ transform: `translate(0px, 88px)` }} src={laptopSvg.src} alt="laptop" />
            }
          </MeasurementItem>
        </div>
      </MeasurementContainer>

      <ProgressContainer isFinished={finished}>
        <ProgressAnimationContainer isFinished={finished}>
          <ProgressAnimation />
        </ProgressAnimationContainer>
        <InfoTextDiv>
          {infoText(state)}
        </InfoTextDiv>
      </ProgressContainer>

      <ButtonContainer style={{ opacity: finished ? 1 : 0 }}>
        <NavigationButton isFocused={true} onClick={() => dispatch(updatePage(PageKey.Result))} isEnabled={finished}>
          <RedButtonDiv>
            {t("Ergebnisse anzeigen")}
          </RedButtonDiv>
        </NavigationButton>
      </ButtonContainer>

      <BottomInfoContainer>
        <BottomInfoDiv>
          {speedtestId !== null && (
            <InfoContainer>
              <InfoKeySpan>{t("Speedtest-ID")}:&nbsp;</InfoKeySpan>
              <CopyableData data={speedtestId}>{speedtestId}</CopyableData>
            </InfoContainer>
          )}
          {!R.isNil(prepareResult?.init.connection.isp) && (
            <>
              <InfoSeparatorSpan>|</InfoSeparatorSpan>
              <InfoContainer>
                <InfoKeySpan>{t("Anbieter")}:&nbsp;</InfoKeySpan>
                <span>{prepareResult?.init.connection.isp}</span>
              </InfoContainer>
            </>
          )}
        </BottomInfoDiv>
      </BottomInfoContainer>
    </RootDiv>
  );
}

export default MeasurementDesktop;
